import {
  Box,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import LoadingState from './LoadingState';

export interface Column<T> {
  /** Stable key for the column. */
  key: string;
  /** Header label. */
  header: ReactNode;
  /** Cell renderer. */
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Marks the value used as the card title on mobile. */
  primary?: boolean;
  /** Hide this column's label on the mobile card (value still renders full-width). */
  hideLabelOnMobile?: boolean;
  /** Do not show this column at all on mobile cards. */
  hideOnMobile?: boolean;
}

interface ResponsiveTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  /** Rendered when there are no rows and not loading. */
  emptyState?: ReactNode;
  /** Optional trailing content for each mobile card (e.g. action buttons). */
  renderMobileActions?: (row: T) => ReactNode;
}

/**
 * Renders a data set as a real table on tablet/desktop and as a stack of
 * readable cards on phones — eliminating the horizontal-scroll problem that
 * plagued every list page. The `primary` column becomes each card's title;
 * the rest render as label / value rows.
 */
function ResponsiveTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  loading,
  emptyState,
  renderMobileActions,
}: ResponsiveTableProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (loading) {
    return <LoadingState count={5} variant={isMobile ? 'list' : 'list'} />;
  }

  if (!rows.length) {
    return <>{emptyState ?? null}</>;
  }

  // ---- Mobile: stacked cards ----
  if (isMobile) {
    const primaryCol = columns.find((c) => c.primary) ?? columns[0];
    const restCols = columns.filter((c) => c !== primaryCol && !c.hideOnMobile);

    return (
      <Stack spacing={1.25}>
        {rows.map((row, i) => (
          <Card
            key={getRowKey(row, i)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            sx={{
              p: 1.75,
              ...(onRowClick && { cursor: 'pointer', '&:active': { bgcolor: 'action.hover' } }),
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: restCols.length ? 1 : 0 }}>
              {primaryCol.render(row)}
            </Typography>
            <Stack spacing={0.75}>
              {restCols.map((col) => (
                <Stack
                  key={col.key}
                  direction="row"
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems="baseline"
                >
                  {!col.hideLabelOnMobile && (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {col.header}
                    </Typography>
                  )}
                  <Box sx={{ textAlign: 'right', minWidth: 0, fontSize: '0.875rem' }}>{col.render(row)}</Box>
                </Stack>
              ))}
            </Stack>
            {renderMobileActions && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>{renderMobileActions(row)}</Box>
            )}
          </Card>
        ))}
      </Stack>
    );
  }

  // ---- Desktop / tablet: table ----
  return (
    <TableContainer component={Card} sx={{ overflowX: 'auto' }}>
      <Table sx={{ minWidth: 560 }}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} align={col.align}>
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow
              key={getRowKey(row, i)}
              hover
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align}>
                  {col.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ResponsiveTable;
